import { useCallback, useEffect, useReducer, useRef } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import { type FileUploadResponseItem } from '@atlassian/conversation-assistant-service';
import {
	InlineInputBoxAbortMessageCommand,
	InlineInputBoxErrorMessageCommand,
	InlineInputBoxFinalResponseMessageCommand,
	InlineInputBoxSendMessageCommand,
	InlineInputBoxTraceMessageCommand,
	ROVO_CHAT_CLIENT_ID,
	ROVO_INLINE_INPUT_BOX_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands';
import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';

type InlineChatControllerOptions = {
	source: string;
	productKey: string;
	onAbort: () => void;
	onComplete: () => void;
	onSubmit?: () => void;
	onFollow?: () => void;
	onCancel?: () => void;
	autoClose?: boolean;
	objectId?: string | number;
};

type InlineChatMode = 'editing' | 'thinking' | 'error' | 'warning' | 'success';
export type InlineChatError = {
	content: string;
	status_code?: number;
	message_template?: string;
};

type InlineChatState = {
	mode: InlineChatMode;
	currentAction: string | null;
	isCancelling: boolean;
	error: InlineChatError | null;
};

const initialState: InlineChatState = {
	mode: 'editing',
	currentAction: null,
	isCancelling: false,
	error: null,
};

type ReducerAction =
	| {
			type: 'submit';
	  }
	| {
			type: 'trace';
			currentAction: string;
	  }
	| {
			type: 'cancel';
	  }
	| {
			type: 'keep';
	  }
	| {
			type: 'abort';
	  }
	| {
			type: 'complete';
	  }
	| {
			type: 'error';
			error: InlineChatError;
	  }
	| {
			type: 'success';
	  };

const reducer = (state: InlineChatState, action: ReducerAction): InlineChatState => {
	switch (action.type) {
		case 'submit':
			return { ...state, mode: 'thinking', currentAction: null, error: null };
		case 'trace':
			return { ...state, currentAction: action.currentAction };
		case 'cancel':
			return { ...state, isCancelling: true };
		case 'keep':
			return { ...state, isCancelling: false };
		case 'abort':
			return { ...state, mode: 'editing', isCancelling: false, currentAction: null, error: null };
		case 'complete':
			return { ...state, mode: 'editing', currentAction: null, error: null };
		case 'error':
			return { ...state, mode: 'error', currentAction: null, error: action.error };
		case 'success':
			return { ...state, mode: 'success', currentAction: null, error: null };
	}
};

type PromptSubmitParams = {
	prompt: DocNode;
	deepResearchEnabled: boolean;
	webSearchEnabled: boolean;
	appFilters?: string[];
	files?: FileUploadResponseItem[];
};

export const useInlineChatController = ({
	source,
	productKey,
	onAbort,
	onSubmit,
	onComplete,
	onCancel,
	autoClose,
	objectId,
}: InlineChatControllerOptions) => {
	const [{ mode, isCancelling, currentAction, error }, dispatch] = useReducer(
		reducer,
		initialState,
	);

	const { ready, send } = useRovoContentBridgeAPI(ROVO_INLINE_INPUT_BOX_CLIENT_ID, (on) => {
		on(InlineInputBoxTraceMessageCommand, ({ message }) => {
			dispatch({ type: 'trace', currentAction: message });
		});

		on(InlineInputBoxFinalResponseMessageCommand, () => {
			dispatch({ type: 'complete' });
			onComplete();
		});

		on(InlineInputBoxErrorMessageCommand, ({ error }) => {
			dispatch({ type: 'error', error: error.message });
		});
	});

	const lastSubmittedInput = useRef<Parameters<typeof send>[1]>();

	useEffect(() => {
		ready();
	}, [ready]);

	const submit = (params: PromptSubmitParams) => {
		const input = {
			...params,
			prompt: params.prompt as any, // TODO: DocNode isn't compatible with the bridge's SerializableValue
			productKey,
			source,
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId: objectId,
		};

		lastSubmittedInput.current = input;
		send(InlineInputBoxSendMessageCommand, input);
		dispatch({ type: 'submit' });
		onSubmit?.();
	};

	const resubmit = () => {
		if (lastSubmittedInput.current) {
			send(InlineInputBoxSendMessageCommand, lastSubmittedInput.current);
			dispatch({ type: 'submit' });
		}
	};

	const abort = useCallback(() => {
		if (mode === 'thinking') {
			send(InlineInputBoxAbortMessageCommand, {
				receiverId: ROVO_CHAT_CLIENT_ID,
			});
		}
		dispatch({ type: 'abort' });
		onAbort();
	}, [mode, send, onAbort]);

	const cancel = useCallback(() => {
		if (!onCancel || mode === 'editing') {
			abort();
		} else {
			dispatch({ type: 'cancel' });
			onCancel();
		}
	}, [mode, abort, dispatch, onCancel]);

	const keep = () => {
		dispatch({ type: 'keep' });
	};

	return {
		mode,
		isCancelling,
		currentAction,
		error,
		submit,
		resubmit,
		cancel,
		keep,
		abort,
		autoClose: autoClose ?? true,
	};
};
